import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { projectsApi, generationsApi } from '../lib/api'
import { GenerationCard } from '../components/GenerationCard'
import { ImageLightbox } from '../components/ImageLightbox'
import { MoveToProjectModal } from '../components/MoveToProjectModal'
import type { Generation, GenerationDetail, ProjectDetail as ProjectDetailType } from '../types'

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [project, setProject] = useState<ProjectDetailType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxImages, setLightboxImages] = useState<Array<{ url: string; id: string; shotNumber: number }>>([])
  const [loadingQuickView, setLoadingQuickView] = useState(false)

  // Move to project modal state
  const [moveModalOpen, setMoveModalOpen] = useState(false)
  const [selectedGeneration, setSelectedGeneration] = useState<Generation | null>(null)

  useEffect(() => {
    if (!id) return

    setLoading(true)
    setError(null)
    projectsApi
      .getOne(id)
      .then(setProject)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [id])

  // Convert project generations to Generation type for GenerationCard
  const generations: Generation[] = project?.generations.map((gen) => ({
    id: gen.id,
    status: gen.status,
    productDescription: gen.productDescription,
    fiveShotMode: gen.images.length > 1,
    lightingStyle: '',
    aspectRatio: '',
    thumbnail: gen.images[0]?.url || null,
    imageCount: gen.images.length,
    projectId: project.id,
    projectName: project.name,
    createdAt: gen.createdAt,
  })) || []

  // Handle quick view
  const handleQuickView = async (generation: Generation) => {
    if (generation.status !== 'COMPLETED') return

    setLoadingQuickView(true)
    try {
      const detail: GenerationDetail = await generationsApi.getOne(generation.id)
      if (detail.images && detail.images.length > 0) {
        setLightboxImages(detail.images.map((img) => ({
          url: img.url,
          id: img.id,
          shotNumber: img.shotNumber,
        })))
        setLightboxOpen(true)
      }
    } catch (err) {
      console.error('Failed to load generation details:', err)
    } finally {
      setLoadingQuickView(false)
    }
  }

  // Handle move to project
  const handleMoveToProject = (generation: Generation) => {
    setSelectedGeneration(generation)
    setMoveModalOpen(true)
  }

  // Handle after move completed - remove from view if moved to different project
  const handleMoved = (projectId: string | null, _projectName: string | null) => {
    if (selectedGeneration && projectId !== project?.id) {
      // Image was moved to a different project (or removed from project)
      // Remove it from the current view
      setProject((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          generations: prev.generations.filter((g) => g.id !== selectedGeneration.id),
        }
      })
    }
  }

  // Handle delete generation
  const handleDelete = async (generation: Generation) => {
    if (!confirm('Delete this generation? This cannot be undone.')) return
    try {
      await generationsApi.delete(generation.id)
      // Remove from local state
      setProject((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          generations: prev.generations.filter((g) => g.id !== generation.id),
        }
      })
    } catch (err) {
      console.error('Failed to delete generation:', err)
    }
  }

  // Handle delete entire project
  const handleDeleteProject = async () => {
    if (!project) return
    const count = project.generations.length
    const message = count > 0
      ? `Delete "${project.name}"? The ${count} generation(s) will be moved to your gallery (not deleted). This cannot be undone.`
      : `Delete "${project.name}"? This cannot be undone.`
    if (!confirm(message)) return
    try {
      await projectsApi.delete(project.id)
      navigate('/projects')
    } catch (err) {
      console.error('Failed to delete project:', err)
    }
  }

  if (loading) {
    return (
      <>
        {/* Header Skeleton */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-10 h-10 rounded-lg bg-surface-muted animate-pulse" />
          <div className="h-8 w-48 bg-surface-muted rounded animate-pulse" />
        </div>
        {/* Grid Skeleton */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="aspect-square rounded-xl bg-surface-muted animate-pulse"
            />
          ))}
        </div>
      </>
    )
  }

  if (error || !project) {
    return (
      <div className="text-center py-16">
        <span className="material-symbols-outlined text-6xl text-red-400 mb-4 block">
          error
        </span>
        <h3 className="text-lg font-medium mb-2">Project not found</h3>
        <p className="text-text-muted mb-6">
          {error || 'The project you are looking for does not exist.'}
        </p>
        <button
          onClick={() => navigate('/projects')}
          className="btn btn-primary"
        >
          Back to Projects
        </button>
      </div>
    )
  }

  return (
    <>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/projects')}
            className="size-10 flex items-center justify-center rounded-lg bg-white border border-border-color text-text-muted hover:text-primary hover:border-primary transition-colors"
            title="Back to projects"
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl md:text-4xl font-semibold text-text-main leading-tight">
                {project.name}
              </h1>
              <button
                onClick={handleDeleteProject}
                className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete project"
              >
                <span className="material-symbols-outlined">delete</span>
              </button>
            </div>
            <p className="text-text-muted text-base mt-1">
              {generations.length} {generations.length === 1 ? 'image' : 'images'}
            </p>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      {generations.length === 0 ? (
        <div className="text-center py-16">
          <span className="material-symbols-outlined text-6xl text-text-light mb-4 block">
            photo_library
          </span>
          <h3 className="text-lg font-medium mb-2">No images yet</h3>
          <p className="text-text-muted mb-6">
            Generate images and assign them to this project
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary"
          >
            Go to AI Studio
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {generations.map((gen) => (
            <GenerationCard
              key={gen.id}
              generation={gen}
              onQuickView={handleQuickView}
              onMoveToProject={handleMoveToProject}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Loading overlay for quick view */}
      {loadingQuickView && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 shadow-lg flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <span className="text-text-main font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* Full-screen Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        initialIndex={0}
      />

      {/* Move to Project Modal */}
      {selectedGeneration && (
        <MoveToProjectModal
          isOpen={moveModalOpen}
          onClose={() => setMoveModalOpen(false)}
          generation={selectedGeneration}
          onMoved={handleMoved}
        />
      )}
    </>
  )
}
